import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  Box,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { processSteps } from "./processSteps";

const CustomStepIcon = ({ active, completed, icon }: any) => {
  const isActive = Boolean(active);
  const isCompleted = Boolean(completed);

  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isCompleted
          ? "linear-gradient(135deg, #5f9156 0%, #7abf6a 100%)"
          : isActive
          ? "linear-gradient(135deg, #803341 0%, #a44a50 100%)"
          : "rgba(126,90,96,0.22)",
        border: isActive
          ? "2px solid rgba(255,255,255,0.82)"
          : "1px solid rgba(128,51,65,0.22)",
        color: "#fff",
        fontFamily: '"Manrope", sans-serif',
        fontWeight: 700,
        fontSize: "0.85rem",
      }}
    >
      {isCompleted ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : icon}
    </Box>
  );
};

export const DesktopStepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(
    Array(processSteps.length).fill(false),
  );
  const [isVisible, setIsVisible] = useState(false);
  const stepperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.4 },
    );

    const stepperNode = stepperRef.current;

    if (stepperNode) {
      observer.observe(stepperNode);
    }

    return () => {
      if (stepperNode) {
        observer.unobserve(stepperNode);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveStep((previousStep) => {
        const nextStep = previousStep + 1;

        setCompletedSteps((previousCompleted) => {
          const updatedCompleted = [...previousCompleted];
          updatedCompleted[previousStep] = true;
          return updatedCompleted;
        });

        if (nextStep === processSteps.length) {
          setTimeout(() => {
            setCompletedSteps(Array(processSteps.length).fill(false));
            setActiveStep(0);
          }, 900);
          return previousStep;
        }

        return nextStep;
      });
    }, 1800);

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <Box
      ref={stepperRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Typography
        component="h3"
        sx={{
          textAlign: { md: "left", lg: "center" },
          mb: { md: 4.2, lg: 5.2 },
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: { md: "2.1rem", lg: "2.4rem" },
          fontWeight: 700,
          color: "#30161b",
          lineHeight: 1.06,
        }}
      >
        A Structured, Transparent Workflow
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: { md: 3.8, lg: 4.8 } }}>
        {processSteps.map((step, index) => (
          <Step key={step.label} completed={completedSteps[index]}>
            <StepLabel
              StepIconComponent={(props) => (
                <CustomStepIcon {...props} completed={completedSteps[index]} />
              )}
              sx={{
                "& .MuiStepLabel-label": {
                  mt: 1.1,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "rgba(56,31,36,0.75)",
                  letterSpacing: "0.02em",
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          p: "20px 20px",
          textAlign: "center",
          background:
            "linear-gradient(152deg, rgba(255,255,255,0.88), rgba(255,247,243,0.76))",
          border: "1px solid rgba(128,51,65,0.14)",
          boxShadow: "0 12px 22px rgba(24,10,12,0.08)",
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", serif',
            fontWeight: 700,
            fontSize: "1.7rem",
            color: "#34181f",
            lineHeight: 1.14,
          }}
        >
          {processSteps[activeStep].label}
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontFamily: '"Manrope", sans-serif',
            color: "rgba(54,33,37,0.82)",
            fontSize: "0.95rem",
            lineHeight: 1.7,
          }}
        >
          {processSteps[activeStep].description}
        </Typography>
      </Paper>
    </Box>
  );
};
